import { createBrowserRouter, Outlet, RouteObject } from 'react-router';
import * as Sentry from '@sentry/react';
import { Page } from '@components/organisms/Page';
import { Paths } from '@src/routes/paths';
import loginLoader from '@src/auth/login.loader';
import registerLoader from '@src/auth/register.loader';
import securedRouteLoader from '@src/auth/secured-route.loader';
import { Result } from '@src/views/Result.view';
import { CallbackView } from '@src/views/Callback.view';
import GlobalErrorHandler from '@components/organisms/GlobalErrorHandler';
import { GenerateSampleCodesView } from '@src/views/internal/GenerateSampleCodes.view';
import securedAdminRouteLoader from '@src/auth/secured-admin-route.loader';
import ToastList from '@components/toasts/toastList';
import { ProfileView } from '@src/views/Profile.view';
import { RootView } from '@src/views/Root.view';
import { RegisterSampleView } from '@src/views/RegisterSample.view';
import { AddSenderAddressView } from '@src/views/AddSenderAddressView';
import { ToastProvider } from '@components/toasts/toast-provider.provider';
import { BaseLayout } from '@components/organisms/BaseLayout';
import { AddSampleCommentView } from '@src/views/AddSampleCommentView';
import { ClaimSampleView } from '@src/views/ClaimSampleView';
import { RefreshView } from '@src/views/Refresh.view.tsx';
import { SilentCallbackView } from '@src/views/SilentCallback.view';
import { ImpersonateProvider } from '@src/contexts/ImpersonateContext';

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export const routes: RouteObject[] = [
  {
    errorElement: <GlobalErrorHandler />,
    HydrateFallback: () => null,
    children: [
      {
        path: Paths.LOGIN,
        loader: loginLoader,
        element: null,
      },
      {
        path: Paths.REGISTER,
        loader: registerLoader,
        element: null,
      },
      {
        path: Paths.CALLBACK,
        element: (
          <Page titleKey="callback">
            <CallbackView />
          </Page>
        ),
      },
      {
        // Renders inside the silent-renew iframe, so no <Page> wrapper: it must
        // not set the document title or mount any app chrome.
        path: Paths.SILENT_CALLBACK,
        element: <SilentCallbackView />,
      },
      {
        path: Paths.REFRESH,
        element: (
          <Page titleKey="refresh">
            <RefreshView />
          </Page>
        ),
      },
      {
        id: 'base',
        element: (
          <ToastProvider>
            <ImpersonateProvider>
              <ToastList />
              <Outlet />
            </ImpersonateProvider>
          </ToastProvider>
        ),
        children: [
          {
            id: 'RegisterSample',
            path: Paths.REGISTER_SAMPLE,
            loader: securedRouteLoader,
            element: (
              <BaseLayout titleKey="activateSampleCode">
                <RegisterSampleView />
              </BaseLayout>
            ),
          },
          {
            id: 'ClaimSample',
            path: Paths.CLAIM_SAMPLE,
            element: (
              <BaseLayout titleKey="activateSample">
                <ClaimSampleView />
              </BaseLayout>
            ),
          },
          {
            id: 'EditSenderAddress',
            path: `${Paths.SENDER_ADDRESS}/:id`,
            element: (
              <BaseLayout titleKey="editSenderAddress">
                <AddSenderAddressView />
              </BaseLayout>
            ),
          },
          {
            id: 'AddSampleComment',
            path: `${Paths.SAMPLE_COMMENT}/:id`,
            element: (
              <BaseLayout titleKey="addSampleComment">
                <AddSampleCommentView />
              </BaseLayout>
            ),
          },
          {
            id: 'interal',
            loader: securedAdminRouteLoader,
            path: Paths.INTERNAL,
            element: <Outlet />,
            children: [
              {
                id: 'generateSampleCodes',
                path: Paths.GENERATE_SAMPLE_CODES,
                element: (
                  <BaseLayout titleKey="generateSampleCodes">
                    <GenerateSampleCodesView />
                  </BaseLayout>
                ),
              },
            ],
          },
          {
            id: 'root',
            path: Paths.PROFILE,
            element: <RootView />,
            children: [
              {
                id: 'profile',
                element: (
                  <BaseLayout titleKey="accountOverview">
                    <ProfileView />
                  </BaseLayout>
                ),
                index: true,
              },
              {
                id: 'Result',
                path: `${Paths.RESULT}/:id`,
                loader: securedRouteLoader,
                element: (
                  <BaseLayout
                    titleKey="resultDetails"
                    parentPage={Paths.PROFILE}
                  >
                    <Result />
                  </BaseLayout>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
];

export const browserRouter = sentryCreateBrowserRouter(routes);
